import { NEW_RECIPE_TANK_DAYS, tankDaysFrom } from "@bl/domain";
import { ChevronLeft, ChevronRight, Repeat as RepeatIcon } from "lucide-react";
import { useState } from "react";
import {
  type BrewSeries,
  type RecipeDetail,
  send,
  useChange,
  useRecipe,
  useRecipes,
  useTanks,
} from "../api";
import { Dialog, DialogForm, ErrorNote, Field } from "../components/ui";
import { showDate, today } from "../format";
import { useNotify } from "../notify";
import { datesFor, MAX_DAYS, monthsLater, type Repeat, repeatsFor } from "../recurrence";
import { capacityLabel } from "../tankLayout";
import { fermenterChoices } from "../tankSchedule";

/**
 * Putting a brew on the schedule (PLAN.md, "Slice 2"; the design Tyler accepted on 2026-10-02). A brew
 * is put on it for a day and a recipe, with no lots chosen and nothing taken; what the schedule plans
 * is worked out from it on the server, never written to inventory. Production's tank schedule shows it
 * (Scott's Jam, 2026-10-08: "Calendar has been replaced by the default production tab"), and To order,
 * under Inventory, says what it needs.
 */

const DAY = 86_400_000;

/** A local date (ISO) moved by whole days. */
export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setTime(d.getTime() + days * DAY);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Whole days from one local date to another. */
export function daysBetween(from: string, to: string): number {
  return Math.round(
    (new Date(`${to}T12:00:00`).getTime() - new Date(`${from}T12:00:00`).getTime()) / DAY,
  );
}

/** The Monday of a date's week, where a month's calendar starts each row. */
export function weekOf(iso: string): string {
  const day = new Date(`${iso}T12:00:00`).getDay();
  return addDays(iso, -((day + 6) % 7));
}

/** The first of a date's month. */
export const monthOf = (iso: string) => `${iso.slice(0, 7)}-01`;

/** The first of the month some months from another's. */
export function addMonths(month: string, months: number): string {
  const total = Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + months;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}-01`;
}

/** The days a month's calendar shows: whole weeks, Monday to Sunday, from its 1st to its last day. */
export function monthGrid(month: string): string[] {
  const end = addDays(weekOf(addDays(addMonths(month, 1), -1)), 6);
  const days: string[] = [];
  for (let d = weekOf(month); d <= end; d = addDays(d, 1)) days.push(d);
  return days;
}

/** Marks a brew that is one of a recurring brew (Tyler, 2026-10-03). */
export function SeriesMark() {
  return (
    <span className="series-mark" title="Part of a recurring brew">
      <RepeatIcon size={13} aria-hidden />
      <span className="sr-only">Recurring</span>
    </span>
  );
}

/**
 * Puts a brew on the schedule: a recipe, the version it brews, and a day or several (Tyler,
 * 2026-10-03). From a recipe's page the recipe is that one; from the schedule it is picked. Schedule
 * recurring widens the dialog to a calendar beside the form, where days are picked month by month or
 * filled in by a way of repeating. Given a series, it changes that recurring brew instead: its
 * version, its rule and the days of its brews still to come.
 */
export function PlanBrewDialog({
  open,
  onClose,
  recipeId,
  on,
  tankId,
  series,
  onSeriesSaved,
}: {
  open: boolean;
  onClose: () => void;
  recipeId?: string;
  /** The day it starts on, as a day clicked on the tank schedule; a week from today otherwise. */
  on?: string;
  /** The fermenter it goes into, as one clicked on the tank schedule. */
  tankId?: string;
  series?: BrewSeries;
  /** The series as changed, to tell whether a brew is still one of it. */
  onSeriesSaved?: (series: BrewSeries) => void;
}) {
  const [recurring, setRecurring] = useState(false);
  const close = () => {
    setRecurring(false);
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      title={series ? "Change the recurring brew" : "Add a brew to the calendar"}
      wide={recurring || Boolean(series)}
    >
      <PlanBrewForm
        onClose={close}
        recipeId={series?.recipe.id ?? recipeId}
        initialOn={on}
        initialTank={tankId}
        recurring={recurring || Boolean(series)}
        onRecurring={setRecurring}
        series={series}
        onSeriesSaved={onSeriesSaved}
      />
    </Dialog>
  );
}

/**
 * Which version a planned brew brews (Tyler, 2026-10-03): the newest on the day, as it is by default,
 * or one kept, the newest as it is now or an older one. `value` is empty for the newest on the day,
 * else the id of the version kept.
 */
export function VersionField({
  versions,
  value,
  onChange,
  disabled,
}: {
  versions: { id: string; version: number; updatedAt: string }[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const [newest, ...older] = versions;
  if (!newest) return null;
  const kept = versions.find((v) => v.id === value);
  return (
    <Field
      label="Version"
      hint={
        kept
          ? `It brews version ${kept.version} on the day, even if a newer one is saved before then.`
          : "It brews whatever version is newest on the day."
      }
    >
      <select
        className="select"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Newest (now version {newest.version})</option>
        <option value={newest.id}>Version {newest.version}, as it is now</option>
        {older.map((v) => (
          <option key={v.id} value={v.id}>
            Version {v.version}, saved {showDate(v.updatedAt)}
          </option>
        ))}
      </select>
    </Field>
  );
}

const without = (set: ReadonlySet<string>, day: string) => {
  const next = new Set(set);
  next.delete(day);
  return next;
};

/** What a series' form starts from: its rule, and the days of its brews still to come. */
function startOf(series: BrewSeries | undefined, initialOn: string | undefined) {
  const on = series?.firstDay ?? initialOn ?? addDays(today(), 7);
  const repeat: Repeat | "" = series && series.repeat !== "days" ? series.repeat : "";
  const remaining = (series?.brews ?? [])
    .filter((b) => b.status === "planned")
    .map((b) => b.brewedOn);
  const until = series?.until ?? remaining.at(-1) ?? monthsLater(on, 3);
  // The calendar opens on the first brew still to come, not on a first day long past.
  const month = monthOf(remaining[0] ?? on);
  if (!series) {
    return { on, repeat, until, month, added: new Set<string>(), removed: new Set<string>() };
  }
  // The days the rule gives that are not still to come were brewed, started or taken off; the days
  // still to come that it does not give were picked or moved one by one.
  const base = repeat ? datesFor(on, repeat, until) : [on];
  return {
    on,
    repeat,
    until,
    month,
    added: new Set(remaining.filter((d) => !base.includes(d))),
    removed: new Set(base.filter((d) => !remaining.includes(d))),
  };
}

function PlanBrewForm({
  onClose,
  recipeId,
  initialOn,
  initialTank,
  recurring,
  onRecurring,
  series,
  onSeriesSaved,
}: {
  onClose: () => void;
  recipeId?: string;
  initialOn?: string;
  initialTank?: string;
  recurring: boolean;
  onRecurring: (on: boolean) => void;
  series?: BrewSeries;
  onSeriesSaved?: (series: BrewSeries) => void;
}) {
  const { data: recipes = [] } = useRecipes();
  const notify = useNotify();
  const [first] = useState(() => startOf(series, initialOn));
  const [chosen, setChosen] = useState(recipeId ?? "");
  const { data: detail } = useRecipe(chosen);
  // Empty brews whatever version is newest on the day; a version's id keeps that one.
  const [kept, setKept] = useState(series?.keepsVersion ? series.recipe.id : "");
  const [on, setOn] = useState(first.on);
  const [repeat, setRepeat] = useState<Repeat | "">(first.repeat);
  const [until, setUntil] = useState(first.until);
  // Days clicked on the calendar beyond what the repeat gives, and days it gives that were clicked off.
  const [added, setAdded] = useState<ReadonlySet<string>>(first.added);
  const [removed, setRemoved] = useState<ReadonlySet<string>>(first.removed);
  const [month, setMonth] = useState(first.month);
  // A recurring brew picks no tank here; each brew books one from its own page or the schedule.
  const [tankId, setTankId] = useState(initialTank ?? "");
  const now = today();

  const versions = detail?.versions ?? [];
  const base = on ? (repeat ? datesFor(on, repeat, until) : [on]) : [];
  // A brew still to come of the series stays where it is even when its day has passed.
  const toCome = new Set(
    (series?.brews ?? []).filter((b) => b.status === "planned").map((b) => b.brewedOn),
  );
  const picked = new Set(
    recurring
      ? [...base, ...added].filter((d) => !removed.has(d) && (d >= now || toCome.has(d)))
      : base.slice(0, 1),
  );
  const days = [...picked].sort();
  const toggle = (day: string) => {
    if (picked.has(day)) {
      setAdded((a) => without(a, day));
      if (base.includes(day)) setRemoved((r) => new Set(r).add(day));
    } else {
      setRemoved((r) => without(r, day));
      if (!base.includes(day)) setAdded((a) => new Set(a).add(day));
    }
  };

  const plan = useChange(
    async (_: undefined, key) => {
      const keepVersion = kept !== "";
      if (series) {
        // The version goes to the series' brews only when it was changed here, so a brew given a
        // version of its own keeps it through a change to the series' days.
        const versionChanged = kept !== (series.keepsVersion ? series.recipe.id : "");
        const saved = await send.editSeries(
          {
            id: series.id,
            patch: {
              ...(versionChanged ? { ...(kept ? { recipeId: kept } : {}), keepVersion } : {}),
              repeat: repeat || "days",
              firstDay: on,
              until: repeat ? until : null,
              days,
            },
          },
          key,
        );
        onSeriesSaved?.(saved);
        return [];
      }
      const recipe = kept || chosen;
      if (days.length === 1) {
        return [
          await send.planBrew(
            {
              recipeId: recipe,
              plannedOn: days[0] as string,
              keepVersion,
              tankId: recurring ? undefined : tankId || undefined,
            },
            key,
          ),
        ];
      }
      return send.planBrews(
        {
          recipeId: recipe,
          days,
          keepVersion,
          repeat: repeat || "days",
          until: repeat ? until : undefined,
        },
        key,
      );
    },
    {
      onSuccess: (brews) => {
        const [one] = brews;
        const last = brews.at(-1);
        if (series) notify("Recurring brew changed");
        else if (one && last) {
          notify({
            title:
              brews.length === 1
                ? `${one.recipe.name} on the calendar for ${showDate(one.brewedOn)}`
                : `${one.recipe.name} on the calendar ${brews.length} times, ${showDate(one.brewedOn)} to ${showDate(last.brewedOn)}`,
            link: { kind: "brew", brewId: one.id },
          });
        }
        onClose();
      },
    },
  );
  const sorted = [...recipes].sort((a, b) => a.name.localeCompare(b.name));
  const tooMany = days.length > MAX_DAYS;
  return (
    <DialogForm
      onSubmit={() => plan.mutate(undefined)}
      footer={
        <>
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="button primary"
            disabled={!chosen || days.length === 0 || tooMany || plan.isPending}
          >
            {series
              ? "Save the series"
              : days.length > 1
                ? `Add ${days.length} brews to calendar`
                : "Add to calendar"}
          </button>
        </>
      }
    >
      <div className={`plan-brew ${recurring ? "recurring" : ""}`}>
        <div className="plan-brew-fields">
          {series ? (
            <p className="muted" style={{ margin: 0 }}>
              Changes the brews of this series whose day has not started. A brew moved on its own
              keeps its day while it is still picked.
            </p>
          ) : null}
          {recipeId ? null : (
            <Field label="Recipe" required>
              <select
                value={chosen}
                onChange={(e) => {
                  setChosen(e.target.value);
                  setKept("");
                }}
              >
                <option value="">Choose a recipe</option>
                {sorted.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <VersionField versions={versions} value={kept} onChange={setKept} />
          <Field
            label={recurring ? "First brew day" : "Brew day"}
            required
            hint="Its lots are chosen when brew day starts."
          >
            <input
              type="date"
              value={on}
              onChange={(e) => {
                const day = e.target.value;
                setOn(day);
                if (day) {
                  setMonth(monthOf(day));
                  if (until < day) setUntil(monthsLater(day, 3));
                }
              }}
            />
          </Field>
          {series || recurring ? null : (
            <FermenterField recipe={detail} on={on} value={tankId} onChange={setTankId} />
          )}
          {series ? null : (
            <label className="check-row">
              <input
                type="checkbox"
                checked={recurring}
                onChange={(e) => onRecurring(e.target.checked)}
              />
              Schedule recurring
            </label>
          )}
          {recurring ? (
            <>
              <Field label="Repeat">
                <select value={repeat} onChange={(e) => setRepeat(e.target.value as Repeat | "")}>
                  <option value="">Only the days I pick</option>
                  {on
                    ? repeatsFor(on).map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.label}
                        </option>
                      ))
                    : null}
                </select>
              </Field>
              {repeat ? (
                <Field label="Until" required>
                  <input
                    type="date"
                    min={on}
                    value={until}
                    onChange={(e) => setUntil(e.target.value)}
                  />
                </Field>
              ) : null}
            </>
          ) : null}
        </div>
        {recurring ? (
          <div className="plan-brew-calendar">
            <MonthCalendar
              month={month}
              onMonth={setMonth}
              picked={picked}
              onToggle={toggle}
              from={now}
            />
            <p className="muted plan-brew-count" aria-live="polite">
              {days.length === 0
                ? "Click the days to brew."
                : `${days.length} ${days.length === 1 ? "brew" : "brews"}${series ? " to come" : ""}: ${showDate(days[0])}${days.length > 1 ? ` to ${showDate(days.at(-1))}` : ""}`}
            </p>
            {tooMany ? (
              <p className="form-error">At most {MAX_DAYS} brews can be added at once.</p>
            ) : null}
          </div>
        ) : null}
      </div>
      <ErrorNote error={plan.error} />
    </DialogForm>
  );
}

/**
 * Which fermenter a brew goes into, picked as it is put on the calendar (the tank schedule's proposal,
 * approved 2026-10-05): the ones free for its days in a fermenter from the brew day that hold its
 * batch, smallest first, then the rest saying why not. Not yet is fine; a tank is booked later from
 * the brew's page or the schedule.
 */
function FermenterField({
  recipe,
  on,
  value,
  onChange,
}: {
  recipe: RecipeDetail | undefined;
  on: string;
  value: string;
  onChange: (tankId: string) => void;
}) {
  const { data: tanks = [] } = useTanks();
  // Its fermentation, dry hop and lager/crash added up; until its recipe has loaded, the days a new
  // recipe starts with.
  const days = recipe
    ? tankDaysFrom({
        fermentation: recipe.fermentationDays,
        dryHop: recipe.dryHopDays,
        lagerCrash: recipe.lagerCrashDays,
        brite: recipe.briteDays,
      }).fermenter
    : NEW_RECIPE_TANK_DAYS.fermenter;
  const choices =
    on && tanks.some((t) => t.kind === "Fermenter")
      ? fermenterChoices(tanks, {
          on,
          days,
          liters: recipe?.settings.batchLiters ?? null,
          recipe: recipe?.name ?? null,
        })
      : [];
  if (choices.length === 0) return null;
  const picked = choices.find((c) => c.tank.id === value);
  return (
    <Field
      label="Fermenter"
      hint={
        picked && !picked.free
          ? // Full or scheduled, it is booked all the same (Scott's Jam, 2026-10-08).
            `${picked.tank.name} ${picked.busy ? `is ${picked.note}` : `${picked.note} then`}. It can still be booked.`
          : `Free for its ${days} days in a fermenter from the brew day, smallest first.`
      }
    >
      <select className="select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Not yet</option>
        {choices.map((c) => (
          <option key={c.tank.id} value={c.tank.id} disabled={c.tank.outOfService}>
            {c.tank.name}, {capacityLabel(c.tank)}
            {c.note ? ` (${c.note})` : ""}
          </option>
        ))}
      </select>
    </Field>
  );
}

const monthFormat = new Intl.DateTimeFormat(undefined, {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});
const dayFormat = new Intl.DateTimeFormat(undefined, {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});
const weekdayLetter = new Intl.DateTimeFormat(undefined, { weekday: "narrow", timeZone: "UTC" });
const utc = (day: string) => new Date(`${day}T00:00:00Z`);

/**
 * A month at a time, stepped through with its arrows, its weeks Monday to Sunday; a click picks a day
 * or puts it back (Tyler, 2026-10-03). Days before today cannot be picked.
 */
function MonthCalendar({
  month,
  onMonth,
  picked,
  onToggle,
  from,
}: {
  /** The first of the month shown. */
  month: string;
  onMonth: (month: string) => void;
  picked: ReadonlySet<string>;
  onToggle: (day: string) => void;
  from: string;
}) {
  const days = monthGrid(month);
  const weeks = Array.from({ length: days.length / 7 }, (_, w) => days.slice(w * 7, w * 7 + 7));
  return (
    <div className="month-calendar">
      <div className="month-calendar-head">
        <button
          type="button"
          className="icon-button"
          aria-label="Previous month"
          onClick={() => onMonth(addMonths(month, -1))}
        >
          <ChevronLeft size={18} />
        </button>
        <strong aria-live="polite">{monthFormat.format(utc(month))}</strong>
        <button
          type="button"
          className="icon-button"
          aria-label="Next month"
          onClick={() => onMonth(addMonths(month, 1))}
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <table className="month-calendar-grid">
        <thead>
          <tr>
            {(weeks[0] ?? []).map((d) => (
              <th key={d} scope="col">
                {weekdayLetter.format(utc(d))}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week) => (
            <tr key={week[0]}>
              {week.map((day) => (
                <td key={day}>
                  {monthOf(day) === month ? (
                    <button
                      type="button"
                      aria-pressed={picked.has(day)}
                      aria-label={dayFormat.format(utc(day))}
                      className={day === from ? "today" : undefined}
                      disabled={day < from}
                      onClick={() => onToggle(day)}
                    >
                      {Number(day.slice(8))}
                    </button>
                  ) : null}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
